import {
  V2ListExpenseCategoriessForAccountApiArg,
  useV2ListExpenseCategoriessForAccountQuery,
} from "@generated";
import type { ExpenseCategory, ExpenseCategoryStatus } from "../types";

export interface UseExpenseCategoriesListArgs {
  page?: V2ListExpenseCategoriessForAccountApiArg["page"];
  filter?: V2ListExpenseCategoriessForAccountApiArg["filter"];
}

export interface ExpenseCategoriesListPageMeta {
  recordCount: number;
  pageCount: number;
}

export const useExpenseCategoriesList = ({
  page,
  filter,
}: UseExpenseCategoriesListArgs) => {
  const { data, error, isLoading, isFetching, refetch } =
    useV2ListExpenseCategoriessForAccountQuery(
      { page, filter, include: ["phase"] },
      // Refetch on remount (e.g. returning from Edit) so saves are reflected.
      { refetchOnMountOrArgChange: true }
    );

  // Only `phase` is included, so every included record is a phase — map id→name.
  const phaseNameById = new Map<string, string>();
  (data?.included ?? []).forEach((inc: any) => {
    phaseNameById.set(String(inc.id), inc.attributes?.name ?? "");
  });

  const rows: ExpenseCategory[] = (data?.data ?? []).map((ec: any) => {
    const phaseId = ec.relationships?.phase?.data?.id
      ? String(ec.relationships.phase.data.id)
      : null;
    return {
      id: String(ec.id),
      name: ec.attributes?.name ?? "",
      rate: ec.attributes?.rate ?? null,
      markup: ec.attributes?.markup ?? null,
      useResourceRate: !!ec.attributes?.["use-resource-rate"],
      status: (ec.attributes?.status ?? "optional") as ExpenseCategoryStatus,
      variable: !!ec.attributes?.variable,
      phaseId,
      phaseName: phaseId ? phaseNameById.get(phaseId) ?? "" : "",
    };
  });

  const pageMeta: ExpenseCategoriesListPageMeta = {
    recordCount: data?.meta?.["record-count"] ?? 0,
    pageCount: data?.meta?.["page-count"] ?? 0,
  };

  return { rows, pageMeta, error, isLoading, isFetching, refetch };
};

export default useExpenseCategoriesList;
